import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { VisualEngine } from '../components/WeatherVisuals';
import { isDemoMode } from '../config/environment';

// Weather Themes Configuration
const THEMES = {
    sunny: { hue: 35, sat: '85%', light: '60%', temp: 28, descKey: 'weather.sunny' },
    rainy: { hue: 220, sat: '30%', light: '40%', temp: 19, descKey: 'weather.rainy' },
    snowy: { hue: 190, sat: '50%', light: '70%', temp: -3, descKey: 'weather.snowy' },
    windy: { hue: 170, sat: '15%', light: '50%', temp: 15, descKey: 'weather.windy' }
};

export default function WeatherLoginPage() {
    const { t } = useTranslation();
    const { login } = useAuth();
    const navigate = useNavigate();

    // State
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [weather, setWeather] = useState('sunny');
    const [temp, setTemp] = useState(26);
    const [isSimpleMode, setIsSimpleMode] = useState(() => {
        if (isDemoMode) return true;
        return localStorage.getItem('simpleMode') === 'true';
    });
    const [currentTime, setCurrentTime] = useState('');

    // Refs
    const containerRef = useRef(null);
    const cardRef = useRef(null);
    const bgCanvasRef = useRef(null);
    const sceneCanvasRef = useRef(null);
    const fxCanvasRef = useRef(null);
    const engineRef = useRef(null);

    // --- Weather Strategy ---
    const fetchWeather = useCallback(async () => {
        if (isDemoMode) {
            setWeather('sunny');
            setTemp(22);
            return;
        }

        try {
            // 1. Try OpenMeteo API (Timeout 3s)
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 3000);

            // Default to Tokyo if geolocation fails or not permitted
            let lat = 35.6895, lon = 139.6917;

            const response = await fetch(
                `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true`,
                { signal: controller.signal }
            );
            clearTimeout(timeoutId);

            if (!response.ok) throw new Error('Weather API failed');

            const data = await response.json();
            const code = data.current_weather.weathercode;
            const currentTemp = data.current_weather.temperature;

            // Map WMO codes to our 4 types
            let weatherType = 'sunny';
            if (code >= 51 && code <= 67) weatherType = 'rainy';
            else if (code >= 71 && code <= 77) weatherType = 'snowy';
            else if (code >= 80 && code <= 82) weatherType = 'rainy';
            else if (code >= 95) weatherType = 'rainy'; // Thunderstorm
            else if (code === 0 || code === 1) weatherType = 'sunny';
            else if (code >= 2 && code <= 48) weatherType = 'windy'; // Cloudy/Foggy -> Windy style

            setWeather(weatherType);
            setTemp(Math.round(currentTemp));

        } catch (err) {
            console.warn('Weather API failed, using fallback:', err.message);
            // 2. Fallback: Deterministic but Season-aware
            const now = new Date();
            const month = now.getMonth(); // 0-11
            const day = now.getDate();
            const types = ['sunny', 'rainy', 'windy', 'sunny', 'snowy', 'rainy'];
            const fallbackWeather = types[day % types.length];

            // 季节性基准温度模拟
            let baseTemp = 18; // 默认春/秋
            if (month <= 1 || month === 11) {
                baseTemp = 6 + (day % 10); // 冬季: 6-15度
            } else if (month >= 5 && month <= 8) {
                baseTemp = 26 + (day % 10); // 夏季: 26-35度
            }

            setWeather(fallbackWeather);
            setTemp(baseTemp);
        }
    }, []);

    // --- Visual Engine Lifecycle ---
    useEffect(() => {
        if (isSimpleMode) return;

        const engine = new VisualEngine(
            {
                bg: bgCanvasRef.current,
                scene: sceneCanvasRef.current,
                fx: fxCanvasRef.current
            },
            () => {
                console.warn('Low FPS detected, switching to Simple Mode');
                alert('パフォーマンス向上のため、簡易モードに切り替えました');
                setIsSimpleMode(true);
                localStorage.setItem('simpleMode', 'true');
            }
        );
        engineRef.current = engine;

        const handleResize = () => {
            if (containerRef.current) {
                const { width, height } = containerRef.current.getBoundingClientRect();
                engine.resize(width, height);
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        engine.start();

        return () => {
            engine.stop();
            window.removeEventListener('resize', handleResize);
        };
    }, [isSimpleMode]);

    // Update Weather in Engine
    useEffect(() => {
        if (engineRef.current && !isSimpleMode) {
            engineRef.current.setWeather(weather);
        }
        // Update CSS Variables for Theme
        const theme = THEMES[weather];
        document.documentElement.style.setProperty('--theme-hue', theme.hue);
        document.documentElement.style.setProperty('--theme-sat', theme.sat);
    }, [weather, isSimpleMode]);

    // Initial Load
    useEffect(() => {
        fetchWeather();
        const timer = setInterval(() => {
            const d = new Date();
            const dateStr = d.toLocaleDateString('ja-JP', { month: 'long', day: 'numeric', weekday: 'short' });
            const timeStr = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
            setCurrentTime(`${dateStr} ${timeStr}`);
        }, 1000);
        return () => clearInterval(timer);
    }, [fetchWeather]);

    // --- 3D Tilt Effect ---
    const handleMouseMove = (e) => {
        if (isSimpleMode || !cardRef.current || !containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width * 2 - 1;
        const y = (e.clientY - rect.top) / rect.height * 2 - 1;

        // Update engine mouse pos
        if (engineRef.current) engineRef.current.setMouse((x + 1) / 2, (y + 1) / 2);

        // CSS Transform
        cardRef.current.style.transform = `perspective(1000px) rotateY(${x * 5}deg) rotateX(${y * -5}deg)`;
    };

    const handleTouchMove = (e) => {
        if (e.touches.length > 0) {
            const touch = e.touches[0];
            // Simulate mouse move with touch coordinates
            // We need to construct a mock event object or extract logic
            if (isSimpleMode || !cardRef.current || !containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            const x = (touch.clientX - rect.left) / rect.width * 2 - 1;
            const y = (touch.clientY - rect.top) / rect.height * 2 - 1;

            if (engineRef.current) engineRef.current.setMouse((x + 1) / 2, (y + 1) / 2);
            cardRef.current.style.transform = `perspective(1000px) rotateY(${x * 5}deg) rotateX(${y * -5}deg)`;
        }
    };

    const handleMouseLeave = () => {
        if (cardRef.current) cardRef.current.style.transform = 'perspective(1000px) rotateY(0deg) rotateX(0deg)';
        if (engineRef.current) engineRef.current.setMouse(0.5, 0.5);
    };

    // --- Login Logic ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        try {
            await login(username, password);
            navigate('/dashboard');
        } catch (err) {
            setError(t('loginFailed'));
        } finally {
            setIsLoading(false);
        }
    };

    const toggleSimpleMode = () => {
        if (isDemoMode) return;

        const newState = !isSimpleMode;
        setIsSimpleMode(newState);
        localStorage.setItem('simpleMode', String(newState));
    };

    return (
        <div
            className="min-h-screen flex items-center justify-center overflow-hidden transition-colors duration-1000"
            style={{
                backgroundColor: isSimpleMode ? '#f3f4f6' : `hsl(${THEMES[weather].hue}, 20%, 10%)`,
                fontFamily: "'Inter', sans-serif"
            }}
        >
            {/* Main Container */}
            <div
                ref={containerRef}
                className="relative w-full max-w-md h-[600px] p-4"
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleMouseLeave}
            >
                {/* The Card */}
                <div
                    ref={cardRef}
                    id="weather-card"
                    className={`w-full h-full relative rounded-[30px] overflow-hidden transition-all duration-500 ${isSimpleMode ? 'shadow-xl bg-white' : 'shadow-2xl bg-gray-900'}`}
                    style={{ transformStyle: 'preserve-3d' }}
                >
                    {/* Canvas Layers (Only in Rich Mode) */}
                    {!isSimpleMode && (
                        <>
                            <canvas ref={bgCanvasRef} className="absolute inset-0 w-full h-full z-0 transition-opacity duration-1000" />
                            <canvas ref={sceneCanvasRef} className="absolute inset-0 w-full h-full z-10 mix-blend-plus-lighter" />
                            <canvas ref={fxCanvasRef} className="absolute inset-0 w-full h-full z-20 pointer-events-none opacity-70 mix-blend-overlay" />
                        </>
                    )}

                    {/* Content Layer */}
                    <div className="relative z-30 h-full flex flex-col justify-between p-8" style={{ background: isSimpleMode ? 'transparent' : 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.4) 100%)' }}>

                        {/* Top Bar */}
                        <div className={`flex justify-between items-start ${isSimpleMode ? 'text-gray-800' : 'text-white'}`}>
                            <div>
                                <h3 className="text-2xl font-bold tracking-wide">{t('appName') || 'TalentApp'}</h3>
                                <p className={`text-sm mt-1 flex items-center gap-2 ${isSimpleMode ? 'text-gray-500' : 'text-white/70'}`}>
                                    {!isSimpleMode && <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse shadow-[0_0_10px_#4ade80]" />}
                                    {t('systemStatus.normal') || 'System Online'}
                                </p>
                            </div>
                            <div className="text-right">
                                <div className="text-lg font-medium opacity-90">{currentTime.split(' ')[0]}</div>
                                <div className="text-2xl font-mono font-bold leading-none">{currentTime.split(' ')[1]}</div>
                            </div>
                        </div>

                        {/* Weather Hero (Center) */}
                        <div className={`flex-1 flex flex-col justify-center pl-2 ${isSimpleMode ? 'text-gray-800' : 'text-white'}`}>
                            <div className="text-7xl font-light leading-none tracking-tighter relative">
                                {temp}<span className="text-4xl align-top opacity-60">°</span>
                            </div>
                            <div className="text-2xl mt-2 font-medium opacity-90">
                                {t(THEMES[weather].descKey)}
                            </div>
                        </div>

                        {/* Login Form (Bottom) */}
                        <div
                            className={`p-6 rounded-3xl backdrop-blur-md border transition-all duration-300 ${isSimpleMode
                                ? 'bg-gray-50 border-gray-200'
                                : 'bg-white/10 border-white/10 hover:bg-white/20 hover:border-white/20'
                                }`}
                        >
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <input
                                        type="text"
                                        placeholder={t('username')}
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        className={`w-full px-4 py-3 rounded-xl outline-none transition-all ${isSimpleMode
                                            ? 'bg-white border border-gray-300 text-gray-900 focus:ring-2 focus:ring-blue-500'
                                            : 'bg-black/20 border border-white/10 text-white placeholder-white/50 focus:bg-black/30 focus:border-white/30'
                                            }`}
                                        required
                                    />
                                </div>
                                <div>
                                    <input
                                        type="password"
                                        placeholder={t('password')}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className={`w-full px-4 py-3 rounded-xl outline-none transition-all ${isSimpleMode
                                            ? 'bg-white border border-gray-300 text-gray-900 focus:ring-2 focus:ring-blue-500'
                                            : 'bg-black/20 border border-white/10 text-white placeholder-white/50 focus:bg-black/30 focus:border-white/30'
                                            }`}
                                        required
                                    />
                                </div>

                                {error && <div className="text-red-400 text-sm text-center font-medium">{error}</div>}

                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className={`w-full py-3 rounded-xl font-bold text-lg transition-all transform active:scale-95 ${isLoading ? 'opacity-70 cursor-not-allowed' : ''
                                        } ${isSimpleMode
                                            ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg'
                                            : 'bg-white text-black hover:bg-white/90 shadow-[0_0_20px_rgba(255,255,255,0.3)]'
                                        }`}
                                >
                                    {isLoading ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                            </svg>
                                            {t('loggingIn')}...
                                        </span>
                                    ) : t('login')}
                                </button>
                            </form>

                            {!isDemoMode && (
                                <div className="mt-4 flex justify-center">
                                    <button
                                        onClick={toggleSimpleMode}
                                        className={`text-xs font-medium transition-colors ${isSimpleMode ? 'text-gray-400 hover:text-gray-600' : 'text-white/40 hover:text-white/80'}`}
                                    >
                                        {isSimpleMode ? t('enableEffects') : t('disableEffects')}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
